<script setup lang="ts">
import { ref } from 'vue'
import { ClientOnly } from '#components'
import { EditorContent } from '@tiptap/vue-3'
import SourceHint from '~/components/ui/SourceHint.vue'
import SourceRichTextLinkPopover from '~/components/ui/SourceRichTextLinkPopover.vue'
import SourceRichTextToolbar from '~/components/ui/SourceRichTextToolbar.vue'
import { useSourceRichTextEditor } from '~/composables/ui/useSourceRichTextEditor'
import { ORG_RICH_TEXT_VISIBLE_MAX } from '~/utils/orgRichText'

defineOptions({ name: 'SourceRichTextEditor' })

type Size = 'xsmall' | 'small' | 'medium'
type Theme = 'light' | 'dark'

type Props = {
  label: string
  theme?: Theme
  size?: Size
  disabled?: boolean
  readonly?: boolean
  placeholder?: string
  modelValue?: string
  maxChars?: number
  error?: string
  helper?: string
  hasCounter?: boolean
  name?: string
  /** État hover Figma figé (page labo / snapshot). */
  previewHover?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  theme: 'light',
  size: 'small',
  disabled: false,
  readonly: false,
  placeholder: 'Text Area',
  modelValue: '',
  maxChars: ORG_RICH_TEXT_VISIBLE_MAX,
  error: '',
  helper: '',
  hasCounter: true,
  name: undefined,
  previewHover: false,
})

const emit = defineEmits<{
  (event: 'update:modelValue', value: string): void
}>()

const linkPopoverOpen = ref(false)
const linkOpenRequest = ref(0)

function onLinkClick(): void {
  linkOpenRequest.value += 1
}

const {
  rteRootEl,
  editor,
  editorReady,
  headingValue,
  visibleCount,
  isFilled,
  canEdit,
  labelId,
  hintId,
  hintText,
  visualSize,
  inlineTools,
  blockTools,
  embedTools,
  onHeadingChange,
} = useSourceRichTextEditor(props, emit, {
  onLinkClick,
  isLinkPopoverOpen: linkPopoverOpen,
})
</script>

<template>
  <div
    ref="rteRootEl"
    class="source-rte"
    :aria-invalid="error ? 'true' : undefined"
    :aria-describedby="hintText ? hintId : undefined"
    :class="[
      visualSize,
      theme,
      {
        'has-error': Boolean(error),
        'is-disabled': disabled,
        'is-filled': isFilled,
        'is-preview-hover': previewHover,
      },
    ]"
    :data-ready="editorReady ? 'true' : 'false'"
  >
    <label :id="labelId">{{ label }}</label>

    <div class="source-rte__field">
      <SourceRichTextToolbar
        :label="label"
        :heading-value="headingValue"
        :can-edit="canEdit"
        :editor-ready="editorReady"
        :inline-tools="inlineTools"
        :block-tools="blockTools"
        :embed-tools="embedTools"
        @heading-change="onHeadingChange"
      />

      <ClientOnly>
        <EditorContent
          :editor="editor"
          class="source-rte__content"
          :class="{ 'is-readonly': readonly }"
        />
        <template #fallback>
          <div class="source-rte__fallback" aria-label="Chargement du composant">Chargement…</div>
        </template>
      </ClientOnly>
    </div>

    <div v-if="hintText" class="source-rte__hint">
      <span
        v-if="error"
        class="material-symbols-rounded source-rte__hint-icon"
        aria-hidden="true"
      >report_problem</span>
      <SourceHint
        :hint-id="hintId"
        :text="hintText"
        :state="error ? 'error' : 'default'"
      />
    </div>
    <p v-if="hasCounter" class="source-rte__counter">{{ visibleCount }} / {{ maxChars }}</p>

    <SourceRichTextLinkPopover
      v-model:open="linkPopoverOpen"
      :editor="editor"
      :can-edit="canEdit"
      :open-request="linkOpenRequest"
      :anchor-element="rteRootEl"
    />
  </div>
</template>

<style lang="scss">
@use '~/assets/scss/partials/components/source-rich-text-editor';
</style>

<style scoped lang="scss">
@use '~/assets/scss/partials/elpdl/spacing' as *;
@use '~/assets/scss/app' as *;

.source-rte.is-disabled .source-rte__hint :deep(.source-hint) {
  color: var(--color-on-disabled);
}

.source-rte.is-disabled :deep(.source-rte__prose) {
  color: var(--color-on-disabled);
}

.source-rte__hint :deep(.source-hint) {
  font-size: 14px;
  line-height: 16px;
}

:deep(.source-rte__prose) {
  min-height: 120px;
  outline: none;
  font-family: 'Public Sans', sans-serif;
  font-size: 16px;
  line-height: 20px;
  color: var(--color-on-surface);
  text-align: left;
}

:deep(.source-rte__prose p) {
  margin: 0 0 $spacing-2xs;
}

:deep(.source-rte__prose p:empty) {
  min-height: 1.25em;
}

:deep(.source-rte__prose h2) {
  margin: 0 0 $spacing-2xs;
  font-family: 'Public Sans', sans-serif;
  font-size: 20px;
  line-height: 24px;
  font-weight: 700;
}

:deep(.source-rte__prose h3) {
  margin: 0 0 $spacing-2xs;
  font-family: 'Public Sans', sans-serif;
  font-size: 16px;
  line-height: 20px;
  font-weight: 700;
}

:deep(.source-rte__prose ul),
:deep(.source-rte__prose ol) {
  margin: 0 0 $spacing-2xs;
  padding-left: $spacing-l;
}

:deep(.source-rte__prose blockquote) {
  margin: 0 0 $spacing-2xs;
  padding-left: $spacing-s;
  border-left: 3px solid var(--color-divider);
}

:deep(.source-rte__prose a) {
  color: var(--color-primary);
  text-decoration: underline;
}

:deep(.source-rte__prose p.is-editor-empty:first-child::before),
:deep(.source-rte__prose.is-editor-empty p:first-child::before),
:deep(.ProseMirror p.is-empty:first-child::before) {
  content: attr(data-placeholder);
  float: left;
  height: 0;
  pointer-events: none;
  color: var(--color-on-surface-light);
}
</style>
